import { RefObject } from 'react';
import DOMPurify from 'dompurify';
import { ReadonlyDeep } from 'type-fest';
import { isAlphanumeric, isDecimal } from 'validator';

import { MessageBubblePosition } from './StepWrapper/helpers';
import {
  BaseWidgetComponent,
  ContextVariableConfigurationFields,
  FormStepComponentText,
} from './types';

export const messageBubblePositionToRadiusMap: Record<
  MessageBubblePosition,
  string
> = {
  'first-in-a-batch': '25px 25px 25px 8px',
  'last-in-a-batch': '8px 25px 25px 25px',
  lone: '25px',
  middle: '8px 25px 25px 8px',
} as const;

// Taken from https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
export const isEmailValid = (email: string): boolean => {
  if (!email.length) return false;
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

// Taken from https://stackoverflow.com/questions/6478875/regular-expression-matching-e-164-formatted-phone-numbers
export const isPhoneNumberValid = (phone_number: string): boolean => {
  if (!phone_number.length) return false;

  const normalizedPhoneNumber = phone_number.replace(/-|\(|\)|\s/g, '');

  // Phone number length around the world ranges from 4 to 13 digits.
  // We use 4 to 14 for backwards compatibility.
  // https://worldpopulationreview.com/country-rankings/phone-number-length-by-country
  const re = /^\+?\d{4,14}$/;
  if (re.test(normalizedPhoneNumber)) return true;
  else return false;
};

export const isNumberValid = (numberAsString: string): boolean => {
  const number = Number(numberAsString);

  return (
    isDecimal(numberAsString) &&
    number >= Number.MIN_SAFE_INTEGER &&
    number <= Number.MAX_SAFE_INTEGER
  );
};

export const isName = (label: string) => /\bname\b/i.test(label);

export const getShortTextErrorMessage = (
  text: string,
  configurationFields: ContextVariableConfigurationFields,
  componentText: FormStepComponentText,
): string => {
  const { is_alphanumeric, max_length, min_length } = configurationFields;
  const textLength = text.length;
  // Length check (either or both can exist)
  let isMinLengthValid = true;
  let isMaxLengthValid = true;
  if (min_length) {
    if (textLength < min_length) {
      isMinLengthValid = false;
    }
  }
  if (max_length) {
    if (textLength > max_length) {
      isMaxLengthValid = false;
    }
  }
  // Check alphanumeric if flag is true else default to true
  const isAlphaNumericValid = is_alphanumeric ? isAlphanumeric(text) : true;
  // Construct string
  let errorMessage = '';
  if (
    !isMinLengthValid &&
    !isMaxLengthValid &&
    min_length &&
    max_length &&
    min_length === max_length
  ) {
    errorMessage +=
      componentText.short_text_equal_error.replace(
        '%s',
        min_length.toString(),
      ) + '\n';
  } else {
    if (min_length && !isMinLengthValid) {
      errorMessage +=
        componentText.short_text_min_error.replace(
          '%s',
          min_length.toString(),
        ) + '\n';
    }
    if (max_length && !isMaxLengthValid) {
      errorMessage +=
        componentText.short_text_max_error.replace(
          '%s',
          max_length.toString(),
        ) + '\n';
    }
  }
  if (!isAlphaNumericValid) {
    errorMessage += componentText.short_text_alphanumeric_error + '\n';
  }
  return errorMessage;
};

// Not allow any whitelist for attributes or tags for HTML
export const sanitizeHtmlForSecurity = (html: string) =>
  DOMPurify.sanitize(html, { USE_PROFILES: { html: false } });

export const getAutocompleteValue = (label: string): string | undefined => {
  type RegexToAutocompleteValueMap = ReadonlyDeep<[RegExp, string][]>;

  const creditCardValues: RegexToAutocompleteValueMap = [
    [/\bcard number\b/i, 'cc-number'],
    [/\bcard expiration date\b/i, 'cc-exp'],
    [/\bcard expiration month\b/i, 'cc-exp-month'],
    [/\bcard expiration year\b/i, 'cc-exp-year'],
    [
      /\b(card security code|card validation code|card verification value|signature panel code|credit card ID)\b/i,
      'cc-csc',
    ],
    [/\b(CSC|CVC|CVV|SPC)\b/, 'cc-csc'],
    [/\bcard type\b/i, 'cc-type'],
  ] as const;

  const creditCardAutocompleteValue = creditCardValues.find(([regex]) =>
    regex.test(label),
  )?.[1];

  if (creditCardAutocompleteValue) {
    return creditCardAutocompleteValue;
  }

  const otherValues: RegexToAutocompleteValueMap = [
    [/\bemail\b/i, 'email'],
    [/\b(tele)?phone number\b/i, 'tel'],
    [/\bhonorific prefix\b/i, 'honorific-prefix'],
    [/\b(given|first) name\b/i, 'given-name'],
    [/\b(additional |middle |fore)name\b/i, 'additional-name'],
    [/\b(family |last |sur)name\b/i, 'family-name'],
    [/\bhonorific suffix\b/i, 'honorific-suffix'],
    [/^(your )?(full |legal )?name$/i, 'name'],
    [/\b(screen name|nickname|handle)\b/i, 'nickname'],
    [/\b(organization|job) title\b/i, 'organization-title'],
    [/\b(username|login)\b/i, 'username'],
    [/\bnew password\b/i, 'new-password'],
    [/\b(current )?password\b/i, 'current-password'],
    [
      /\b(one-time|1-time|2fa) (code|password|pin|authorization code)\b/i,
      'one-time-code',
    ],
    [/\b(organization|company)( name)\b/i, 'organization'],
    [/\b(state|canton|post town)\b/i, 'address-level1'],
    [/\b(city|town|village)\b/i, 'address-level2'],
    [/\b(street )?address\b/i, 'street-address'],
    [/\bcountry code\b/i, 'country'],
    [/\bcountry( name)?\b/i, 'country-name'],
    [/\b(postal|post|zip) code\b/i, 'postal-code'],
    [/\bcurrency\b/i, 'transaction-currency'],
    [/\b(price|transaction amount)\b/i, 'transaction-amount'],
    [/\blanguage\b/i, 'language'],
    [/\b(day of birth|birth day)\b/i, 'bday-day'],
    [/\b(month of birth|birth month)\b/i, 'bday-month'],
    [/\b(year of birth|birth year)\b/i, 'bday-year'],
    [/\b(date of birth|birthday)\b/i, 'bday'],
    [/\b(sex|gender|gender identity)\b/i, 'sex'],
    [/\bURL\b/, 'url'],
    [/\b(photo|image|icon|picture)\b/i, 'photo'],
  ] as const;

  const mainAutocompleteValue = otherValues.find(([regex]) =>
    regex.test(label),
  )?.[1];

  if (!mainAutocompleteValue) {
    return undefined;
  }

  type OtherAutocompleteValues = (typeof otherValues)[number][1];

  const creditCardNames: Readonly<OtherAutocompleteValues[]> = [
    'name',
    'given-name',
    'additional-name',
    'family-name',
  ] as const;

  if (
    creditCardNames.includes(mainAutocompleteValue) &&
    /\bcredit card\b/i.test(label)
  ) {
    return `cc-${mainAutocompleteValue}`;
  }

  const namesWithLocation: Readonly<OtherAutocompleteValues[]> = [
    'address-level1',
    'address-level2',
    'street-address',
    'country',
    'country-name',
    'postal-code',
    'tel',
    'email',
  ] as const;

  if (namesWithLocation.includes(mainAutocompleteValue)) {
    if (/\b(home|personal)\b/i.test(label)) {
      return `home ${mainAutocompleteValue}`;
    }

    if (/\b(work|corporate)\b/i.test(label)) {
      return `work ${mainAutocompleteValue}`;
    }
  }

  const shippingOrBillingNames: Readonly<OtherAutocompleteValues[]> = [
    ...namesWithLocation,
    'name',
  ] as const;

  if (shippingOrBillingNames.includes(mainAutocompleteValue)) {
    if (/\bshipping\b/i.test(label)) {
      return `shipping ${mainAutocompleteValue}`;
    }

    if (/\bbilling\b/i.test(label)) {
      return `billing ${mainAutocompleteValue}`;
    }
  }

  return mainAutocompleteValue;
};

export const getEmailAutocompleteValue = (label: string): string => {
  const autocompleteValue = 'email';

  if (/\b(home|personal)\b/i.test(label)) {
    return `home ${autocompleteValue}`;
  }

  if (/\b(work|corporate)\b/i.test(label)) {
    return `work ${autocompleteValue}`;
  }

  if (/\bshipping\b/i.test(label)) {
    return `shipping ${autocompleteValue}`;
  }

  if (/\bbilling\b/i.test(label)) {
    return `billing ${autocompleteValue}`;
  }

  return autocompleteValue;
};

export function isObjectEmpty(object: Record<string, unknown>): boolean {
  return Object.keys(object).length === 0;
}

export const setFocusOnFormSubmit = (
  ref: RefObject<HTMLFormElement> | null,
): void => {
  const formPreviousSibling =
    ref?.current && ref.current.previousElementSibling;
  if (formPreviousSibling) (formPreviousSibling as HTMLElement).focus();
};

export const getBaseStepFields = (widgetComponent: BaseWidgetComponent) => {
  return {
    contextVariables: widgetComponent.context_variables || {},
    isSent: Boolean(widgetComponent.is_sent),
  };
};
